import { useEffect, useRef, useState } from 'react';
import { D, formatMoney } from '@makila/fiscal';
import { X } from 'lucide-react';
import { normalizeSerial, variantGrid } from '@makila/pos-core';
import type { Product } from '../screens/Sale';
import { readScale, scaleConfigured } from '../lib/hw/scale';

function Shell(p: { title: string; onClose: () => void; children: React.ReactNode }) {
  const dlg = useRef<HTMLDialogElement>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);
  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-auto w-[min(560px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">{p.children}</div>
    </dialog>
  );
}

/** Stock do modelo nas lojas (precisa de rede): o desta loja e o das outras. */
export interface StoreStock { storeId: string; stores: { id: string; code: string; name: string }[]; levels: { storeId: string; productId: string; quantity: string }[] }

/**
 * Escolha da variante de um modelo. Com grelha (moda): a cor e depois o
 * tamanho, em dois toques (um só, se houver uma cor). Com rede, cada tamanho
 * mostra o stock desta loja; o que só há noutra loja pede-se lá.
 */
export function VariantDialog(p: {
  model: Product; variants: Product[]; onPick: (v: Product) => void; onClose: () => void;
  stock?: () => Promise<StoreStock>; onRequest?: (fromStoreId: string, v: Product, note: string) => Promise<void>;
}) {
  const g = variantGrid(p.model, p.variants);
  const [color, setColor] = useState<string | null>(g.colors.length === 1 ? g.colors[0]! : null);
  const [st, setSt] = useState<StoreStock | 'offline' | null>(null);
  const [ask, setAsk] = useState<{ v: Product; storeId: string; storeName: string; note: string } | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (p.stock) p.stock().then(setSt, () => setSt('offline')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const qty = (v: Product, store: string) => {
    if (!st || st === 'offline') return null;
    const l = st.levels.find((x) => x.productId === v.id && x.storeId === store);
    return l ? new D(l.quantity) : new D(0);
  };
  const elsewhere = (v: Product) => (st && st !== 'offline' ? st.stores.filter((s) => s.id !== st.storeId).map((s) => ({ s, n: qty(v, s.id)! })).filter((x) => x.n.greaterThan(0)) : []);
  const pickColor = !!g.colors.length && g.colors.length > 1;
  const sizes = g.sizes.length ? g.sizes : [null];
  const n = (d: InstanceType<typeof D>) => d.toString().replace('.', ',');

  return (
    <Shell title={p.model.name} onClose={p.onClose}>
      {sent && <p role="status" className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">{sent}</p>}
      {err && <p role="alert" className="mb-3 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">{err}</p>}
      {ask ? (
        <form onSubmit={async (e) => {
          e.preventDefault();
          try { await p.onRequest!(ask.storeId, ask.v, ask.note); setSent(`Pedido enviado: ${ask.v.variantLabel ?? ask.v.name} da ${ask.storeName}. A gestão envia-o com uma transferência.`); setAsk(null); setErr(null); }
          catch (x) { setErr((x as Error).message); }
        }}>
          <p className="font-semibold">Pedir {ask.v.variantLabel ?? ask.v.name} à {ask.storeName}</p>
          <label className="mt-3 block text-sm font-medium" htmlFor="req-note">Para quem? (opcional)</label>
          <input id="req-note" autoFocus maxLength={200} value={ask.note} onChange={(e) => setAsk({ ...ask, note: e.target.value })} placeholder="Nome e telefone do cliente" className="mt-1 h-12 w-full rounded-lg border border-base bg-[var(--surface)] px-3" />
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => setAsk(null)} className="h-12 flex-1 rounded-xl border border-base font-semibold">Voltar</button>
            <button className="h-12 flex-1 rounded-xl bg-brand-700 font-semibold text-white">Enviar pedido</button>
          </div>
        </form>
      ) : (
        <>
          {pickColor && (
            <div className="mb-4">
              <div className="mb-1.5 text-sm font-medium text-muted">Cor</div>
              <div className="flex flex-wrap gap-2">
                {g.colors.map((c) => (
                  <button key={c} type="button" aria-pressed={color === c} onClick={() => setColor(c)}
                    className={`h-12 min-w-20 rounded-xl border-2 px-4 font-semibold ${color === c ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          {(g.sizes.length > 0 || g.colors.length > 0) && (
            <>
              {g.sizes.length > 0 && <div className="mb-1.5 text-sm font-medium text-muted">Tamanho{pickColor && !color ? ' — escolha primeiro a cor' : ''}</div>}
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {(pickColor && !color ? [] : sizes).map((s) => {
                  const v = g.cell(s, g.colors.length ? color : null);
                  if (!v) return <div key={s ?? '-'} className="grid min-h-20 place-items-center rounded-xl border border-dashed border-base text-muted">{s}</div>;
                  const here = qty(v, st && st !== 'offline' ? st.storeId : '');
                  const out = elsewhere(v);
                  return (
                    <div key={v.id} className="flex flex-col gap-1">
                      <button onClick={() => p.onPick(v)} aria-label={`${v.variantLabel ?? v.name}${here ? `, ${n(here)} em stock` : ''}`}
                        className={`surface flex min-h-20 flex-col justify-between rounded-xl p-3 text-left hover:border-brand-500 ${here && here.lessThanOrEqualTo(0) ? 'opacity-60' : ''}`}>
                        <span className="text-lg font-bold">{s ?? v.variantColor ?? v.variantLabel}</span>
                        <span className="num text-sm text-muted">{formatMoney(v.unitPrice)} MT</span>
                        {here && <span className={`text-xs font-medium ${here.lessThanOrEqualTo(0) ? 'text-brand-700' : 'text-green-700 dark:text-green-400'}`}>{here.lessThanOrEqualTo(0) ? 'Esgotado aqui' : `${n(here)} aqui`}</span>}
                      </button>
                      {here && here.lessThanOrEqualTo(0) && p.onRequest && out.slice(0, 2).map(({ s: o, n: k }) => (
                        <button key={o.id} type="button" onClick={() => setAsk({ v, storeId: o.id, storeName: o.name, note: '' })}
                          className="h-10 rounded-lg border border-base px-2 text-xs font-medium hover:border-brand-500">Pedir à {o.name} ({n(k)})</button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {g.others.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {g.others.map((v) => (
                <button key={v.id} onClick={() => p.onPick(v)} className="surface flex min-h-20 flex-col justify-between rounded-xl p-3 text-left hover:border-brand-500">
                  <span className="font-semibold">{v.variantLabel ?? v.name}</span>
                  <span className="num text-sm text-muted">{formatMoney(v.unitPrice)} MT</span>
                </button>
              ))}
            </div>
          )}
          {st === 'offline' && <p className="mt-3 text-xs text-muted">Sem rede: o stock das lojas não está disponível.</p>}
        </>
      )}
    </Shell>
  );
}

const KG = /^\d{1,4}([.,]\d{1,3})?$/;

/** Peso de um artigo vendido ao quilo, quando não vem de uma etiqueta da balança. */
export function WeightDialog(p: { product: Product; onWeight: (kg: string) => void; onClose: () => void }) {
  const [v, setV] = useState('');
  // Balança de caixa ligada: lê-a ao abrir e a pedido; o peso só serve quando estável.
  const withScale = scaleConfigured();
  const [scale, setScale] = useState<{ busy: boolean; note: string | null }>({ busy: false, note: null });
  const read = async () => {
    setScale({ busy: true, note: null });
    try {
      const r = await readScale();
      if (!r) setScale({ busy: false, note: 'Sem leitura da balança: ponha o artigo no prato e leia outra vez' });
      else if (!r.stable) setScale({ busy: false, note: `A balança ainda não estabilizou (${r.kg.replace('.', ',')} kg): leia outra vez` });
      else { setV(r.kg.replace('.', ',')); setScale({ busy: false, note: null }); }
    } catch (e) { setScale({ busy: false, note: (e as Error).message }); }
  };
  useEffect(() => { if (withScale) void read(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ok = KG.test(v.trim()) && new D(v.trim().replace(',', '.')).greaterThan(0);
  const kg = ok ? new D(v.trim().replace(',', '.')) : null;
  return (
    <Shell title={`Peso — ${p.product.name}`} onClose={p.onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (kg) p.onWeight(kg.toString()); }}>
        <div className="flex items-center gap-2">
          <input autoFocus inputMode="decimal" aria-label="Peso em quilos" placeholder="0,000" value={v} onChange={(e) => setV(e.target.value)}
            className="num h-14 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-3xl" />
          <span className="text-2xl font-bold">kg</span>
        </div>
        {withScale && (
          <button type="button" disabled={scale.busy} onClick={() => void read()} className="mt-2 h-12 w-full rounded-xl border border-base font-semibold disabled:opacity-40">{scale.busy ? 'A ler a balança…' : 'Ler da balança'}</button>
        )}
        {scale.note && <p role="alert" className="mt-2 text-sm text-brand-800">{scale.note}</p>}
        <p className="num mt-2 text-muted">{formatMoney(p.product.unitPrice)} MT/kg{kg ? ` · ${formatMoney(kg.times(p.product.unitPrice).toDecimalPlaces(2))} MT` : ''}</p>
        <button disabled={!kg} className="mt-4 h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Juntar à venda</button>
      </form>
    </Shell>
  );
}

const MONEY = /^\d{1,9}([.,]\d{1,2})?$/;

/** Artigo de preço livre (serviço à medida, peça única): o operador escreve o preço desta venda. */
export function PriceDialog(p: { product: Product; onPrice: (price: string) => void; onClose: () => void }) {
  const [v, setV] = useState(new D(p.product.unitPrice).isZero() ? '' : new D(p.product.unitPrice).toFixed(2).replace('.', ','));
  const ok = MONEY.test(v.trim()) && new D(v.trim().replace(',', '.')).greaterThan(0);
  return (
    <Shell title={`Preço — ${p.product.name}`} onClose={p.onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (ok) p.onPrice(new D(v.trim().replace(',', '.')).toFixed(2)); }}>
        <div className="flex items-center gap-2">
          <input autoFocus inputMode="decimal" aria-label="Preço desta venda" placeholder="0,00" value={v} onChange={(e) => setV(e.target.value)}
            className="num h-16 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-3xl" />
          <span className="text-2xl font-bold">MT</span>
        </div>
        <p className="mt-2 text-sm text-muted">{p.product.priceIncludesTax ? 'Com IVA incluído' : 'Sem IVA (acresce)'} · fica registado com o seu nome</p>
        <button disabled={!ok} className="mt-4 h-16 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Juntar à venda</button>
      </form>
    </Shell>
  );
}

/** Bebidas alcoólicas e tabaco: o operador confirma a idade uma vez por venda. */
export function AgeDialog(p: { product: Product; onConfirm: () => void; onClose: () => void }) {
  return (
    <Shell title="Confirmar a idade" onClose={p.onClose}>
      <p className="text-lg"><b>{p.product.name}</b> só se vende a maiores de 18 anos.</p>
      <p className="mt-1 text-muted">Em caso de dúvida, peça um documento de identificação.</p>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <button onClick={p.onClose} className="h-16 rounded-xl border border-base font-semibold">Não vender</button>
        <button autoFocus onClick={p.onConfirm} className="h-16 rounded-xl bg-brand-700 font-bold text-white">Tem 18 anos ou mais</button>
      </div>
    </Shell>
  );
}

/** Número de série ou IMEI da unidade vendida (leitor de código de barras ou à mão); o IMEI confere-se. */
export function SerialDialog(p: { product: Product; taken: string[]; onSerial: (serial: string) => void; onClose: () => void }) {
  const [v, setV] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Shell title={`Número de série — ${p.product.name}`} onClose={p.onClose}>
      <form onSubmit={(e) => {
        e.preventDefault();
        try {
          const sn = normalizeSerial(v);
          if (p.taken.includes(sn)) throw new Error(`O número ${sn} já está nesta venda`);
          p.onSerial(sn);
        } catch (err) { setError((err as Error).message); }
      }}>
        <input autoFocus aria-label="Número de série" placeholder="Leia ou escreva o IMEI / n.º de série" value={v} onChange={(e) => { setV(e.target.value.toUpperCase()); setError(null); }}
          className="h-16 w-full rounded-xl border border-base bg-surface-2 px-4 font-mono text-2xl" />
        <p className={error ? 'mt-2 text-sm font-semibold text-brand-700' : 'mt-2 text-sm text-muted'}>{error ?? `Uma unidade por número.${p.product.warrantyMonths ? ` Garantia de ${p.product.warrantyMonths} meses a partir de hoje.` : ''}`}</p>
        <button disabled={!v.trim()} className="mt-4 h-16 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Juntar à venda</button>
      </form>
    </Shell>
  );
}
